import React from 'react';
import {COLORS} from '../styles/index';
import {SystemIcons} from '../icons';
import {ErrorMessage, InputWrapper, SearchIconWrapper} from './styling';
import {IconButton} from '../Button';
import SearchBarInput from './components/SearchBarInput';
import SearchField from './components/SearchField';
import {Size} from '../types';
import styled, { useTheme } from 'styled-components';
import { DropdownContent, DropdownItem } from '../Dropdown';
import { useClickOutsideRef } from '../common';

export type SearchBarProps = {
  /**
   * Required. The ID of the search bar.
   */
  id: string;

  /**
   * Optional. The current search term in the search bar.
   */
  searchTerm?: string;
  /**
   * Required. A function to be called when the search term changes.
   * It should take a string representing the new search term.
   */
  setSearchTerm: (term: string) => void;
  /**
   * Required. A function to be called when the enter key is pressed in the search bar.
   * It should take an event object as a parameter.
   */
  enterSearch: (e: any) => void;
  /**
   * Required. A function to be called when the search term is removed.
   * It should take an event object as a parameter.
   */
  removeSearch: (e: any) => void;
  /**
   * Optional. The placeholder text to be displayed in the search bar when it is empty.
   */
  placeholder?: string;
  /**
   * Optional. The label for the search button in the search bar.
   */
  performSearchLabel?: string;
  /**
   * Optional. A boolean indicating whether the search bar is disabled.
   */
  disabled?: boolean;
  /**
   * Optional. The validation message to be displayed when the search bar is in an error state.
   */
  validationMessage?: string;
  /**
   * Optional. A function to be called when a keydown event occurs in the search bar.
   */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /**
   * Optional. The size of the search bar. Can be 'Small' or 'Medium'.
   */
  size?: Size.Small | Size.Medium;
  /**
   * Optional. The margin of the search bar. Can be any valid CSS margin value.
   */
  margin?: string;
  /**
   * Optional. A boolean indicating whether the dropdown in the search bar should be shown.
   */
  showDropdown?: boolean;
  /**
   * Optional. The icon for the dropdown button in the search bar.
   */
  dropdownButtonIcon?: React.ReactNode;
  /**
   * Optional. The label for the dropdown button in the search bar.
   */  
  dropdownButtonLabel?: string;
  /**
   * Optional. The variant of the dropdown button in the search bar. Can be 'primary', 'secondary', or 'tertiary'.
   */  
  dropdownButtonVariant?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Optional. A function to be called when the dropdown button in the search bar is clicked.
   */  
  dropdownButtonAction?: () => boolean | void | undefined;
  /**
   * Optional. An array of DropdownItem objects representing the items in the dropdown.
   */
  searchList?: DropdownItem[];
  /**
   * Optional. A function to be called when an item is added to the dropdown.
   * It should take a DropdownItem object as a parameter.
   */
  addToSearchList?: (e: DropdownItem) => void;
  /**
   * Optional. A boolean indicating whether the dropdown in the search bar is scrollable.
   */
  dropdownScrollable?: boolean;
};

const ClearIconWrapper = styled.div`
  display: flex;
  align-items: center;

  &.hidden {
    display: none !important;
  }
`;

const SearchBar: React.FunctionComponent<SearchBarProps> = ({
                                                             id,
                                                             searchTerm,
                                                             setSearchTerm,
                                                             enterSearch,
                                                             removeSearch,
                                                             placeholder,
                                                             performSearchLabel,
                                                             disabled,
                                                             validationMessage,
                                                             onKeyDown,
                                                             size,
                                                             margin,
                                                             showDropdown,
                                                             dropdownButtonIcon,
                                                             dropdownButtonLabel,
                                                             dropdownButtonVariant,
                                                             dropdownButtonAction,
                                                             searchList=[],
                                                             addToSearchList,
                                                             dropdownScrollable
                                                           }) => {
  const containerRef = React.useRef<any>(null);
  const theme = useTheme();
  const topLevelRef = React.useRef<any>(null);

  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useClickOutsideRef(()=>setIsOpen(false), [containerRef, topLevelRef]);

  const isPressingEnter = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  };

  const enteredSearchTerm = (e: any) => {
    addToSearchList && addToSearchList({value:e.target.value});
    enterSearch(e);
  }

  const dropdownAction = () => {
    if(dropdownButtonAction)
      dropdownButtonAction();    
  }

  return (
    <>
      <InputWrapper
        $disabled={disabled}
        ref={topLevelRef}
        $margin={margin}>
        <SearchField id={'id'} size={size}
                     ref={containerRef}
                     searchTerm={searchTerm}
                     disabled={disabled}
                     validationMessage={validationMessage}>
          <SearchBarInput
            placeholder={disabled ? '' : placeholder}
            id={id}
            size={size}
            disabled={disabled}
            setSearchTerm={(term: string) => setSearchTerm(term)}
            searchTerm={searchTerm}
            onKeyDown={onKeyDown}
            enterSearch={enteredSearchTerm}
            focusParentRefs={[containerRef]}
            onClick={()=>setIsOpen(!isOpen)}
            ref={dropdownRef}
          />
          <SearchIconWrapper className={size ? size : ''} aria-label={performSearchLabel}>
            <IconButton id={`${id}_Search`}
                        variant="secondary"
                        shape="circular"
                        action={enterSearch}
                        disabled={disabled}
                        onKeyPress={(e: any) => (isPressingEnter(e) ? enterSearch(e) : null)}>
              <SystemIcons.Search size="24px"/>
            </IconButton>
          </SearchIconWrapper>
          <ClearIconWrapper className={(size ? size : '').concat(!searchTerm ? ' hidden' : '')}>
            <IconButton id={`${id}_Clear`}
                        variant="secondary"
                        shape="circular"
                        action={removeSearch}
                        disabled={disabled}
                        onKeyPress={(e: any) => (isPressingEnter(e) ? removeSearch(e) : null)}>
              <SystemIcons.Clear size="24px"/>
            </IconButton>
          </ClearIconWrapper>
        </SearchField>

      {showDropdown && <DropdownContent id={'search'}
                                        containerRef={containerRef}
          size={size}
          isButton={false}
          ariaRolesType="input"
          customizationProps={{
            items:searchList,
            action: dropdownAction,
            actionLabel: dropdownButtonLabel,
            actionIcon: dropdownButtonIcon,
            actionVariant: dropdownButtonVariant,
            onValueUpdate:(e)=>{},
            scrollable: dropdownScrollable,
            pinTopItem: true
          }}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          filter={''}
          selectedValues={searchTerm ? [searchTerm] : []}
          setSelectedValues={(e)=>setSearchTerm(e[0])}
          messageOnNoResults={''}
          focused={null}
          setFocused={()=>{}}
        />}

      </InputWrapper>
      {validationMessage && (
        <ErrorMessage className={size || ''}>
          <SystemIcons.TechnicalWarning color={COLORS.generateToken({componentType:'text', defaultVariant:'critical'}, theme)} className={size || ''}/>
          <span>{validationMessage}</span>
        </ErrorMessage>
      )}
    </>
  );
};

export default SearchBar;
